import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  CreateAccountData,
  CreateAccountResponse,
} from '../utils/types';
import { authAPI, accountAPI } from '../services/api';
import toast from 'react-hot-toast';

type UserUpdate = Partial<Omit<User, 'verification'>> & {
  verification?: Partial<NonNullable<User['verification']>>;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  register: (data: RegisterData) => Promise<AuthResponse>;
  createAccount: (data: CreateAccountData) => Promise<CreateAccountResponse>;
  logout: () => void;
  updateUser: (data: UserUpdate) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  );
  const [loading, setLoading] = useState(true);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<AuthResponse> => {
      setLoading(true);
      try {
        const response = await authAPI.login(credentials);

        if (response.token && response.refreshToken) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('refreshToken', response.refreshToken);

          if (response.user) {
            const completeUserData = {
              ...response.user,
              verification: response.verification ?? null,
              bankDetails: response.bankDetails ?? null,
            };

            setUser(completeUserData);
            localStorage.setItem('user', JSON.stringify(completeUserData));
          }

          setToken(response.token || null);

          return response;
        } else {
          throw new Error('Invalid response from server');
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const register = useCallback(
    async (data: RegisterData): Promise<AuthResponse> => {
      setLoading(true);
      try {
        const response = await authAPI.register(data);

        if (response.token && response.refreshToken) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('refreshToken', response.refreshToken);
        }

        if (response.user) {
          setUser(response.user);
        }

        setToken(response.token || null);
        return response;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createAccount = useCallback(
    async (data: CreateAccountData): Promise<CreateAccountResponse> => {
      setLoading(true);
      try {
        const response = await accountAPI.createAccount(data);

        toast.success('Account created successfully!');
        return response;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Account creation failed';
        toast.error(errorMessage);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    const response = await authAPI.logout();
    if (response?.status === true || response?.success === true) {
      setUser(null);
      setToken(null);
      localStorage.clear();
      toast.success(response.message || 'Logged out successfully');
    } else {
      throw new Error(response?.message || 'Logout failed');
    }
  }, []);

  const updateUser = useCallback((data: UserUpdate) => {
    setUser(prev => {
      if (!prev) {
        if (data.id && data.email) {
          localStorage.setItem('user', JSON.stringify(data));
          return data as User;
        }
        return null;
      }

      const updatedUser = {
        ...prev,
        ...data,
      };

      if (data.verification) {
        updatedUser.verification = {
          ...(prev.verification || {}),
          ...data.verification,
        } as User['verification'];
      }

      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser as User;
    });
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (storedToken) {
        try {
          const storedUser = localStorage.getItem('user');
          const storedUserObj = storedUser ? JSON.parse(storedUser) : null;

          const userId =
            storedUserObj?.id || storedUserObj?._id || storedUserObj?.userId;

          if (userId) {
            const userResponse = await authAPI.getProfile(userId);
            if (userResponse) {
              setUser(prev => {
                const prevStr = JSON.stringify(prev);
                const nextStr = JSON.stringify(userResponse);
                if (prevStr === nextStr) {
                  return prev;
                }
                localStorage.setItem('user', nextStr);
                return userResponse;
              });
            }
          }
        } catch (error) {
          if (storedRefreshToken) {
            try {
              const refreshResponse =
                await authAPI.refreshToken(storedRefreshToken);

              if (refreshResponse.token) {
                localStorage.setItem('token', refreshResponse.token);
                setToken(refreshResponse.token);

                const userId = JSON.parse(
                  localStorage.getItem('user') || '{}'
                )?.id;
                if (userId) {
                  const userResponse = await authAPI.getProfile(userId);
                  if (userResponse) {
                    setUser(prev => {
                      if (
                        JSON.stringify(prev) === JSON.stringify(userResponse)
                      ) {
                        return prev;
                      }
                      localStorage.setItem(
                        'user',
                        JSON.stringify(userResponse)
                      );
                      return userResponse;
                    });
                  }
                }
              }
            } catch (refreshError) {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              setToken(null);
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      register,
      createAccount,
      logout,
      updateUser,
      loading,
    }),
    [user, token, login, register, createAccount, logout, updateUser, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
